import 'amazon-connect-streams'
import { defineStore } from 'pinia'
import { useConfigStore } from '@/legacy/store/modules/config'
import { Disposition } from '@/legacy/types/communications/callDispositions'
import {
  BaseCommunicationPayload,
  ConnectCallPayload,
  CommunicationType,
  ConsolidatedCommunicationPayload,
  ConsolidatedCommunicationPatchPayload,
  SubCommunicationPayload,
  SubCommunicationType,
} from '@/legacy/types/communications/communications'
import {
  Direction,
  SpeakingWithType,
} from '@/legacy/types/communications/shared'
import { createCrudApi } from '@/libs/api/crud'
import { UUID } from '@/libs/typing/base'
import { ConsolidatedCommunication } from '@/pages/PatientProfile/CommunicationSidebar/shared/types'
import { useFlagStore } from '@/store/flags/flags'
import {
  AWSContactAttributes,
  CCP_SOFTPHONE_WINDOW_NAME,
  MISSED_CALL_RESET_DELAY_MS,
} from './types'

// Pending missed-call auto-reset timer. Module-scoped because it's a transient
// handle, not reactive UI state. Only ever one at a time — one session has one
// agent, and a fresh miss clears any prior timer before scheduling a new one.
let missedCallResetTimer: ReturnType<typeof setTimeout> | null = null

export const amazonConnectStore = defineStore('amazonConnect', {
  state: () => ({
    currentConnectContact: null as connect.Contact | null,
    agentARN: null as string | null,
    isConnected: false,
    showIncomingCallPopup: false,
    ccpIsOpen: false,
    // Communication ID for an outbound call awaiting CCP contact data
    pendingOutboundCommId: null as string | null,
  }),
  getters: {
    hasIncomingCall(state): boolean {
      return !!state.currentConnectContact?.isInbound()
    },
    connectCallPayload(state): ConnectCallPayload | undefined {
      const contact = state.currentConnectContact
      const contactId = contact?.getContactId()
      if (!state.agentARN || !contactId) return undefined
      return {
        agentArn: state.agentARN,
        queueArn: contact?.getQueue()?.queueARN ?? '',
        contactId,
      }
    },
  },
  actions: {
    initCCP(
      containerDiv: HTMLElement,
      overrides: Partial<connect.InitCCPOptions> = {}
    ) {
      const configVals = useConfigStore().configVals
      if (!configVals) {
        throw new Error(
          'Cannot setup CCP instance before config store is initialized'
        )
      }
      const ccpInitOptions = {
        ccpUrl: configVals.amazonCCP.thymecareInstanceUrl, // REQUIRED
        region: configVals.amazonCCP.instanceRegion, // REQUIRED for `CHAT`, optional otherwise
        loginPopup: true,
        loginUrl: configVals.amazonCCP.thymecareLoginUrl,
        softphone: {
          allowFramedSoftphone: false,
        },
        pageOptions: {
          enableAudioDeviceSettings: true,
          enableVideoDeviceSettings: false,
          enablePhoneTypeSettings: false,
        },
        logConfig: {
          logLevel: connect.LogLevel.DEBUG,
        },
        ...overrides,
        // Merged rather than replaced so a caller can override `autoClose`
        // alone without silently dropping the login popup's geometry.
        loginOptions: {
          autoClose: false,
          height: 600,
          width: 400,
          top: 0,
          left: 0,
          ...overrides.loginOptions,
        },
      }
      // Logged without the options object on purpose — it carries the
      // instance and login URLs. Which window owns the audio session is the
      // part worth having in a support console. See CT-821.
      console.debug('Initializing Amazon Connect CCP', {
        allowFramedSoftphone: ccpInitOptions.softphone.allowFramedSoftphone,
      })
      connect.core.initCCP(containerDiv, ccpInitOptions)
      connect.agent((agent) => {
        this.agentARN = agent.getConfiguration().agentARN
        agent.onStateChange((stateChange) => {
          this.resetStatusAfterMissedCall(agent, stateChange.newState)
        })
        // Also evaluate the state the agent is ALREADY in. An agent who starts
        // their shift already stuck in the missed state produces no further
        // transition for onStateChange to catch, so transitions alone would
        // leave them stuck. This also covers the case where the agent was
        // initialized before we subscribed (connect.agent() invokes its
        // callback immediately when connect.agent.initialized is already true,
        // by which point the initial state change has already fired).
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        this.resetStatusAfterMissedCall(agent, agent.getState().name)
      })
    },
    /**
     * Auto-reset an agent back to Available after they miss a call.
     *
     * Only applies to agents whose routing profile is opted in via
     * `amazonCCP.resetMissedCallRoutingProfileArns`, and is gated behind the
     * `resetMissedCallStatus` flag so it only runs where it's been rolled out.
     * Agents outside those profiles keep Connect's default behavior, where a
     * missed call leaves them non-routable until they manually go Available.
     *
     * Called both when the agent transitions into the missed state and once at
     * agent init with whatever state they're already in — an agent who starts
     * their shift already stuck emits no transition to react to.
     *
     * The reset is deferred by {@link MISSED_CALL_RESET_DELAY_MS} rather than
     * fired immediately: an immediate reset re-offers the still-active missed
     * call to the now-Available agent, who misses it again — a miss→reset
     * loop. Waiting until the contact has moved on avoids that. When the timer
     * fires we re-check the agent's live state and only reset if they're still
     * stuck in the missed state (they may have gone Available on their own, or
     * answered a newer call in the meantime).
     * @param agent
     * @param currentStateName
     */
    resetStatusAfterMissedCall(agent: connect.Agent, currentStateName: string) {
      if (!useFlagStore().resetMissedCallStatus) return

      // Only the missed-call state; every other state is left alone. State
      // names are plain strings, so compare against the enum's string value
      // rather than the enum member itself.
      const missedCallState: string = connect.AgentErrorStates.MISSED_CALL_AGENT
      if (currentStateName !== missedCallState) return

      // Opted-in routing profiles only. The empty-ARN guard means an env var
      // left unset can't match an agent with no routing profile ARN.
      const resetProfileArns =
        useConfigStore().configVals?.amazonCCP
          .resetMissedCallRoutingProfileArns ?? []
      const routingProfileArn = agent.getRoutingProfile().routingProfileARN
      if (!routingProfileArn || !resetProfileArns.includes(routingProfileArn)) {
        return
      }

      // A new miss supersedes any pending reset — restart the clock so the
      // delay is always measured from the most recent miss.
      if (missedCallResetTimer) clearTimeout(missedCallResetTimer)
      missedCallResetTimer = setTimeout(() => {
        missedCallResetTimer = null

        // Re-check live state: only reset if they're STILL stuck in the missed
        // state. If they recovered (went Available, or answered a later call)
        // during the delay, resetting now could stomp an active contact.
        //
        // Must read getState() here, not getAvailabilityState(): onStateChange's
        // `newState` comes from snapshot.state.name (the fine-grained state,
        // e.g. "MissedCallAgent"), and getState() returns that same object.
        // getAvailabilityState() reads a different field (agentAvailabilityState)
        // that does NOT carry the missed-call error state, so comparing against
        // it always fails and the reset never fires.
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        if (agent.getState().name !== missedCallState) return

        const availableState = agent
          .getAgentStates()
          .find((state) => state.type === connect.AgentStateType.ROUTABLE)

        if (!availableState) {
          console.error(
            'agent.getAgentStates() returned no routable state; cannot reset agent — check instance status config'
          )
          return
        }

        agent.setState(availableState, {
          failure: (err) => {
            console.error(
              `failed to reset agent state after missed call (routing profile ${routingProfileArn})`,
              err
            )
          },
        })
      }, MISSED_CALL_RESET_DELAY_MS)
    },
    /**
     * Attach connect call data to a pending outbound communication.
     * Uses the CRUD API directly because this callback runs outside
     * Vue's setup context where Vue Query hooks are unavailable.
     */
    async attachOutboundConnectData() {
      // Guard against attaching inbound call data to an unrelated outbound comm.
      // Inbound calls create their own comm with Connect data already attached
      // during the answerCall() flow in InboundCallPopup/CallActions/controller.ts.
      if (this.currentConnectContact?.isInbound()) {
        this.pendingOutboundCommId = null
        return
      }

      const commId = this.pendingOutboundCommId
      if (!commId) return

      const connectCallPayload = this.connectCallPayload
      if (!connectCallPayload) return

      try {
        await amazonConnectCommunicationCrudApi.partialUpdate({
          ids: [commId],
          body: { connectCall: connectCallPayload },
        })
        this.pendingOutboundCommId = null
      } catch (err) {
        console.error(
          `Failed to attach Connect data to communication ${commId}:`,
          err
        )
      }
    },
    initCallEvents() {
      // bind state here to ignore 'this' binding in functions
      const state = this.$state
      connect.contact((contact) => {
        // queued callbacks triggers incoming event before contact is available on the agent, so we need to check for both
        contact.onIncoming(() => {
          state.showIncomingCallPopup = true
          state.currentConnectContact = contact
        })

        // connecting event happens before incoming event during inbound calls, so we need to set the same handlers for both events to ensure the popup shows up
        contact.onConnecting(() => {
          state.currentConnectContact = contact
          if (contact.isInbound()) {
            state.showIncomingCallPopup = true
          }
          void this.attachOutboundConnectData()
        })
        // connected event is triggered after the agent accepts the call, we are catching both to ensure the state is updated correctly in all scenarios
        contact.onConnected(() => {
          state.currentConnectContact = contact
          state.isConnected = true
          state.showIncomingCallPopup = false
          void this.attachOutboundConnectData()
        })
        contact.onAccepted(() => {
          state.isConnected = true
          state.showIncomingCallPopup = false
          void this.attachOutboundConnectData()
        })
        contact.onMissed(() => {
          state.showIncomingCallPopup = false
          state.isConnected = false
          state.currentConnectContact = null
          state.pendingOutboundCommId = null
        })
        contact.onEnded(() => {
          state.isConnected = false
          state.currentConnectContact = null
          state.pendingOutboundCommId = null
          void this.attachOutboundConnectData()
        })
      })
    },
    hideInboundCallPopup() {
      this.showIncomingCallPopup = false
    },
    setPendingOutboundCommId(commId: string | null) {
      this.pendingOutboundCommId = commId
    },
    startCall(phoneNumber: string, queueARN?: string) {
      const endpoint = connect.Endpoint.byPhoneNumber(phoneNumber)
      const agent = new connect.Agent()
      this.toggleCCPMinimized(true)
      agent.connect(endpoint, {
        queueARN,
        success: () => {
          console.log('outbound call connected')
        },
        failure: (err) => {
          // No contact was created, so no call data is coming. Release the
          // pending comm rather than leaving it to claim the agent's next call.
          this.pendingOutboundCommId = null
          console.error('outbound call connection failed', err)
        },
      })
    },
    toggleCCPMinimized(newValue?: boolean) {
      if (typeof newValue === 'boolean') {
        this.ccpIsOpen = newValue
      } else {
        this.ccpIsOpen = !this.ccpIsOpen
      }
    },
    getContactAttributes() {
      return (
        this.currentConnectContact?.getAttributes() ||
        ({} as AWSContactAttributes)
      )
    },
    /**
     * Whether the dedicated softphone popup currently holds its Web Lock
     * (i.e. is open and alive). See CT-821.
     */
    async isSoftphoneWindowOpen(): Promise<boolean> {
      if (!navigator.locks) {
        return false
      }
      const { held } = await navigator.locks.query()
      return !!held?.some((lock) => lock.name === CCP_SOFTPHONE_WINDOW_NAME)
    },
  },
})

const amazonConnectCommunicationCrudApi = createCrudApi<
  ConsolidatedCommunication,
  never,
  ConsolidatedCommunicationPayload,
  ConsolidatedCommunicationPatchPayload
>('/api/communications/consolidated')

/**
 * Create communication and disposition for inbound call.
 * Uses the CRUD API directly because this runs outside Vue's setup context
 * where Vue Query hooks are unavailable.
 * @param patientId
 * @param staffEntityId
 * @param isIdentityVerified
 */
export async function createCommunicationQuery(
  patientId: UUID,
  staffEntityId: string | null,
  isIdentityVerified: boolean = false
) {
  const connectCallPayload = amazonConnectStore().connectCallPayload
  const commPayload: BaseCommunicationPayload = {
    responsibleStaffId: staffEntityId,
    notes: '',
    type: CommunicationType.Call,
    completedDatetime: new Date().toISOString(),
    patientIds: [patientId],
  }
  const dispositionPayload: SubCommunicationPayload = {
    direction: Direction.inbound,
    disposition: Disposition.ANSWERED,
    isIdentityVerified,
    speakingWithType: SpeakingWithType.member,
    speakingWithPersonId: patientId,
    subCommType: SubCommunicationType.CALL_DISPOSITION,
  }

  const payload: ConsolidatedCommunicationPayload = {
    communication: commPayload,
    subCommunication: dispositionPayload,
    ...(connectCallPayload ? { connectCall: connectCallPayload } : {}),
  }

  return await amazonConnectCommunicationCrudApi.create({ body: payload })
}
